import { useCallback, useState } from 'react';

import {
    getClipboardReadErrorMessage,
    getChecklistToastScope,
    getClipboardWriteErrorMessage,
    parseChecklistFromPlainText,
    serializeChecklistToMarkdown,
} from '@features/Checklist/lib/helpers';
import { IChecklistTask } from '@features/Checklist/model/types';
import { showToast } from '@features/ToastManager/lib/showToast';
import { usePaste } from '@shared/hooks';

interface IAppendChecklistItemInput {
    text: string;
    isChecked: boolean;
}

interface IUseChecklistClipboardParams {
    checklist: IChecklistTask[];
    positionGuid: string;
    isEditMode: boolean;
    markCopied: () => void;
    appendChecklistItems: (items: IAppendChecklistItemInput[]) => boolean;
    moveChecklistItemToBottom: (clientId: string) => void;
}

export const useChecklistClipboard = ({
    checklist,
    positionGuid,
    isEditMode,
    markCopied,
    appendChecklistItems,
    moveChecklistItemToBottom,
}: IUseChecklistClipboardParams) => {
    const toastScope = getChecklistToastScope(positionGuid);
    const [copiedChecklistText, setCopiedChecklistText] = useState('');
    const [copiedChecklistFallbackSourceText, setCopiedChecklistFallbackSourceText] = useState<
        string | null
    >(null);
    const [shouldUseCopiedChecklistFallback, setShouldUseCopiedChecklistFallback] = useState(false);

    const resetCopiedChecklistState = useCallback(() => {
        setCopiedChecklistText('');
        setCopiedChecklistFallbackSourceText(null);
        setShouldUseCopiedChecklistFallback(false);
    }, []);

    const clearClipboardAfterPaste = useCallback(async () => {
        resetCopiedChecklistState();

        if (!navigator.clipboard?.writeText) {
            return;
        }

        try {
            await navigator.clipboard.writeText('');
        } catch {
            console.error('error while clear clipboard afterPaste');
        }
    }, [resetCopiedChecklistState]);

    const handlePasteFromClipboardText = useCallback(
        (text: string) => {
            const parsedChecklistLines = parseChecklistFromPlainText(text);

            if (parsedChecklistLines.length === 0) {
                return false;
            }

            return appendChecklistItems(
                parsedChecklistLines.map((line) => ({
                    text: line,
                    isChecked: false,
                }))
            );
        },
        [appendChecklistItems]
    );

    const handlePasteCopiedChecklistText = useCallback(() => {
        if (!copiedChecklistText || !shouldUseCopiedChecklistFallback) {
            return false;
        }

        return handlePasteFromClipboardText(copiedChecklistText);
    }, [copiedChecklistText, handlePasteFromClipboardText, shouldUseCopiedChecklistFallback]);

    const getPriorityClipboardText = useCallback(
        (clipboardText: string) => {
            const normalizedClipboardText = clipboardText.trim();

            if (!shouldUseCopiedChecklistFallback || !copiedChecklistText) {
                return normalizedClipboardText;
            }

            if (!normalizedClipboardText) {
                return copiedChecklistText;
            }

            if (
                copiedChecklistFallbackSourceText &&
                normalizedClipboardText === copiedChecklistFallbackSourceText.trim()
            ) {
                return copiedChecklistText;
            }

            setShouldUseCopiedChecklistFallback(false);
            setCopiedChecklistFallbackSourceText(null);

            return normalizedClipboardText;
        },
        [copiedChecklistFallbackSourceText, copiedChecklistText, shouldUseCopiedChecklistFallback]
    );

    const handleCopy = useCallback(async () => {
        const text = serializeChecklistToMarkdown(checklist);

        if (!text) {
            return false;
        }

        setCopiedChecklistText(text);

        let clipboardTextBeforeCopy: string | null = null;

        if (navigator.clipboard?.readText) {
            try {
                clipboardTextBeforeCopy = await navigator.clipboard.readText();
            } catch {
                clipboardTextBeforeCopy = null;
            }
        }

        if (!navigator.clipboard?.writeText) {
            showToast(getClipboardWriteErrorMessage(), {
                scope: toastScope,
                type: 'error',
            });
            setCopiedChecklistFallbackSourceText(clipboardTextBeforeCopy);
            setShouldUseCopiedChecklistFallback(true);
            markCopied();
            return true;
        }

        try {
            await navigator.clipboard.writeText(text);
            setCopiedChecklistFallbackSourceText(null);
            setShouldUseCopiedChecklistFallback(false);
            markCopied();
            return true;
        } catch (error) {
            showToast(getClipboardWriteErrorMessage(), {
                scope: toastScope,
                type: 'error',
            });
            console.error('Failed to write checklist to system clipboard', error);
            setCopiedChecklistFallbackSourceText(clipboardTextBeforeCopy);
            setShouldUseCopiedChecklistFallback(true);
            markCopied();
            return true;
        }
    }, [checklist, markCopied, toastScope]);

    const handlePasteButtonClick = useCallback(async () => {
        if (navigator.clipboard?.readText) {
            try {
                const clipboardText = await navigator.clipboard.readText();
                const text = getPriorityClipboardText(clipboardText);

                if (text) {
                    const isPasted = handlePasteFromClipboardText(text);

                    return isPasted;
                }
            } catch (error) {
                showToast(getClipboardReadErrorMessage(), {
                    scope: toastScope,
                    type: 'error',
                });
                console.error('Failed to read checklist from system clipboard', error);
                console.error('error while passed to clipboard');
            }
        } else {
            showToast(getClipboardReadErrorMessage(), {
                scope: toastScope,
                type: 'error',
            });
        }

        const isPasted = handlePasteCopiedChecklistText();

        if (isPasted) {
            await clearClipboardAfterPaste();
        }

        return isPasted;
    }, [
        clearClipboardAfterPaste,
        getPriorityClipboardText,
        handlePasteCopiedChecklistText,
        handlePasteFromClipboardText,
        toastScope,
    ]);

    const handlePaste = useCallback(
        (event: ClipboardEvent) => {
            const { target } = event;
            const isChecklistModalTitle =
                target instanceof HTMLElement &&
                target.closest('[data-checklist-modal-title="true"]') !== null;
            const focusedChecklistItemClientId =
                target instanceof HTMLElement
                    ? target.getAttribute('data-checklist-item-client-id')
                    : null;
            const shouldMoveFocusedEmptyItemToBottom =
                target instanceof HTMLTextAreaElement &&
                isChecklistModalTitle &&
                target.value.trim().length === 0 &&
                typeof focusedChecklistItemClientId === 'string';

            if (
                target instanceof HTMLElement &&
                !isChecklistModalTitle &&
                (target.isContentEditable ||
                    target instanceof HTMLInputElement ||
                    target instanceof HTMLTextAreaElement)
            ) {
                return;
            }

            const clipboardText = event.clipboardData?.getData('text/plain') ?? '';
            const text = getPriorityClipboardText(clipboardText);

            if (!text) {
                return;
            }

            if (!handlePasteFromClipboardText(text)) {
                return;
            }

            if (shouldMoveFocusedEmptyItemToBottom && focusedChecklistItemClientId) {
                moveChecklistItemToBottom(focusedChecklistItemClientId);
            }

            event.preventDefault();
            void clearClipboardAfterPaste();
        },
        [
            clearClipboardAfterPaste,
            getPriorityClipboardText,
            handlePasteFromClipboardText,
            moveChecklistItemToBottom,
        ]
    );

    usePaste(isEditMode ? handlePaste : null);

    return {
        handleCopy,
        handlePasteButtonClick,
    };
};
